import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { AoSGameState } from '../../types';
import { moveGoods } from '../moves/moveGoods';
import { upgradeLoco } from '../moves/upgradeLoco';
import { passMoveGoods } from '../moves/passMoveGoods';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 5: 商品輸送フェーズ
 *
 * 各プレイヤーは最大2回アクションできる（輸送 or 機関車強化）
 * アクション総数 = 輸送回数 + 機関車強化フラグ（0 or 1）
 *
 * @see docs/specifications/logic/common/phase_05_move_goods.md
 */
export const moveGoodsPhase: PhaseConfig<AoSGameState> = {
  moves: {
    moveGoods,
    upgradeLoco,
    passMoveGoods,
    undoToTurnStart,
  },

  turn: {
    onMove: ({ G, events }: { G: AoSGameState; events: any }) => {
      // Move実行後、全プレイヤーが上限回数の手番完了したらターン終了
      const maxDeliveries = G.maxDeliveriesThisPhase ?? 2;
      const allCompleted = Object.values(G.players).every(player =>
        player.isBankrupt || player.turnsCompletedThisPhase >= maxDeliveries
      );

      if (allCompleted) {
        debug.log('[moveGoodsPhase] All players completed, ending turn to trigger phase transition');
        events.endTurn();
      }
    }
  },

  onBegin: ({ G }: { G: AoSGameState; ctx: Ctx }) => {
    debug.log('[moveGoodsPhase] Phase 5: Move Goods started');

    // デフォルト上限を設定（マップ固有設定がなければ 2）
    if (G.maxDeliveriesThisPhase === undefined) {
      G.maxDeliveriesThisPhase = 2;
    }

    // プレイヤーごとのPhase 5状態をリセット
    Object.values(G.players).forEach(player => {
      player.hasUpgradedLocoThisTurn = false;
      player.goodsTransportedThisPhase = 0;
      player.turnsCompletedThisPhase = 0;
    });

    debug.log(`[moveGoodsPhase] Players ready for goods transport (max ${G.maxDeliveriesThisPhase} turns per player)`);
  },

  endIf: ({ G, ctx }: { G: AoSGameState; ctx: Ctx }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[moveGoodsPhase] Force transition to issueShares (Undo)');
      return true;
    }

    // 全プレイヤーが上限回数の手番完了、または倒産
    const maxDeliveries = G.maxDeliveriesThisPhase ?? 2;
    const playerStatuses = Object.keys(G.players).map(playerId => {
      const player = G.players[playerId];
      const completed = player.isBankrupt || player.turnsCompletedThisPhase >= maxDeliveries;

      return {
        playerId,
        isBankrupt: player.isBankrupt,
        turnsCompleted: player.turnsCompletedThisPhase,
        maxDeliveries,
        goodsTransported: player.goodsTransportedThisPhase,
        upgraded: player.hasUpgradedLocoThisTurn,
        completed
      };
    });

    debug.log('[moveGoodsPhase] endIf check:', {
      turn: ctx.turn,
      phase: ctx.phase,
      numMoves: ctx.numMoves,
      playerStatuses
    });

    const allPlayersCompleted = playerStatuses.every(status => status.completed);

    if (allPlayersCompleted) {
      debug.log('[moveGoodsPhase] ★ Phase 5 ending - next should be: collectIncome');
      debug.log('[moveGoodsPhase] ★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });
      return true;
    }

    return false;
  },

  onEnd: ({ ctx }: { G: AoSGameState; ctx: Ctx }) => {
    debug.log('[moveGoodsPhase] ★★★ onEnd called - Phase 5 ended');
    debug.log('[moveGoodsPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });
    debug.log('[moveGoodsPhase] ★★★ Next phase should be: collectIncome');
  },

  // Phase 6 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: AoSGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'collectIncome';
  }
};
